import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuthUserPermissions } from "@/api/resources/auth/hooks";
import { useCurrentDeployment, useFilteredDeployments } from "@/api/resources/deployments/hooks";
import { useCurrentWorkspace } from "@/api/resources/workspaces/hooks";
import { useGlobalFilters } from "@/hooks/useGlobalFilters";
import { DeploymentRole, WorkspaceRole } from "@/supabase.types";

export interface WorkspaceScope {
  workspace_id?: string;
}

export interface DeploymentScope {
  deployment_id?: string;
}

export enum Permission {
  Edit = "edit",
  View = "view",
}

export enum FeaturePermission {
  Cases = "cases",
  CasesReport = "cases-report",
  Maintenances = "maintenances",
  PartsDevice = "parts-device",
}

interface PermissionLookup {
  [Permission.Edit]?: boolean;
  [Permission.View]?: boolean;
}
type EntityPermissionLookup = Record<string, PermissionLookup>; // key is workspace_id OR deployment_id
type FeatureLookup = Record<FeaturePermission, PermissionLookup>; // key is typeof FeaturePermission (cases, maintenances, ...)
type FeaturePermissionLookup = Record<string, FeatureLookup>; // key is workspace_id OR deployment_id

export const useRoles = () => {
  const { t } = useTranslation();
  const { data: userPermissions } = useAuthUserPermissions();
  const { options } = useGlobalFilters();
  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: currentDeployment } = useCurrentDeployment();
  const { data: filteredDeployments } = useFilteredDeployments();

  const availableWorkspaceRoles = useMemo(
    () => [
      { value: WorkspaceRole.admin, label: t("admin"), description: t(`role-explanation.workspace.admin`) },
      { value: WorkspaceRole.viewer, label: t("viewer"), description: t(`role-explanation.workspace.viewer`) },
    ],
    [t],
  );
  const availableDeploymentRoles = useMemo(
    () => [
      { value: DeploymentRole.admin, label: t("admin"), description: t(`role-explanation.deployment.admin`) },
      {
        value: DeploymentRole.technician,
        label: t("technician"),
        description: t(`role-explanation.deployment.technician`),
      },
      {
        value: DeploymentRole.end_customer,
        label: t("end_customer"),
        description: t(`role-explanation.deployment.end_customer`),
      },
      { value: DeploymentRole.viewer, label: t("viewer"), description: t(`role-explanation.deployment.viewer`) },
    ],
    [t],
  );

  const globalPermissions = useMemo(() => {
    // typescript hack to allow for key in permission
    const permissions: PermissionLookup = {
      [Permission.Edit]: false,
      [Permission.View]: false,
    };
    if (userPermissions?.globalAdmin) {
      permissions[Permission.Edit] = true;
      permissions[Permission.View] = true;
    }
    return permissions;
  }, [userPermissions?.globalAdmin]);

  const workspacePermissions = useMemo(() => {
    let permissions: EntityPermissionLookup = {};
    const featurePermissions: FeaturePermissionLookup = {};

    if (!userPermissions?.workspace) {
      return {
        permissions,
        featurePermissions,
      };
    }

    for (const { workspace_id, role } of userPermissions.workspace) {
      if (role === WorkspaceRole.admin) {
        permissions = {
          ...permissions,
          [workspace_id]: {
            ...permissions[workspace_id],
            [Permission.Edit]: true,
          },
        };
      }
      if (role === WorkspaceRole.viewer) {
        permissions = {
          ...permissions,
          [workspace_id]: {
            ...permissions[workspace_id],
            [Permission.View]: true,
          },
        };
      }
    }

    return { permissions, featurePermissions };
  }, [userPermissions?.workspace]);

  const deploymentPermissions = useMemo(() => {
    let permissions: EntityPermissionLookup = {};
    let featurePermissions: FeaturePermissionLookup = {};

    if (!userPermissions?.deployment) {
      return {
        permissions,
        featurePermissions,
      };
    }

    for (const { deployment_id, role } of userPermissions.deployment) {
      if (role === DeploymentRole.admin) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.Edit]: true,
          },
        };
      }
      if (role === DeploymentRole.end_customer) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
        featurePermissions = {
          ...featurePermissions,
          [deployment_id]: {
            ...featurePermissions[deployment_id],
            [FeaturePermission.CasesReport]: {
              [Permission.Edit]: true,
            },
          },
        };
      }
      if (role === DeploymentRole.technician) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
        featurePermissions = {
          ...featurePermissions,
          [deployment_id]: {
            ...featurePermissions[deployment_id],
            [FeaturePermission.CasesReport]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.Cases]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.Maintenances]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.PartsDevice]: {
              [Permission.Edit]: true,
            },
          },
        };
      }
      if (role === DeploymentRole.viewer) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
      }
    }

    return { permissions, featurePermissions };
  }, [userPermissions?.deployment]);

  const hasGlobalPermission = useCallback((perm: Permission) => !!globalPermissions[perm], [globalPermissions]);
  const hasWorkspacePermission = useCallback(
    (perm: Permission, { workspace_id }: WorkspaceScope, feature?: FeaturePermission) => {
      let permitted = false;
      if (!workspace_id) return permitted;
      const { permissions, featurePermissions } = workspacePermissions;

      if (workspace_id in permissions) {
        permitted = !!permissions[workspace_id][perm];
      }

      if (feature && workspace_id in featurePermissions && feature in featurePermissions[workspace_id]) {
        permitted = !!featurePermissions[workspace_id][feature];
      }

      return permitted;
    },
    [workspacePermissions],
  );
  const hasDeploymentPermission = useCallback(
    (perm: Permission, { deployment_id }: DeploymentScope, feature?: FeaturePermission) => {
      let permitted = false;
      if (!deployment_id) return permitted;
      const { permissions, featurePermissions } = deploymentPermissions;

      if (deployment_id in permissions) {
        permitted = !!permissions[deployment_id][perm];
      }

      if (feature && deployment_id in featurePermissions && feature in featurePermissions[deployment_id]) {
        permitted = !!featurePermissions[deployment_id][feature];
      }

      return permitted;
    },
    [deploymentPermissions],
  );

  const hasPermission = useCallback(
    (permission: Permission, context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      if (hasGlobalPermission(permission)) return true;
      if (context) {
        if ("workspace_id" in context && hasWorkspacePermission(permission, context, feature)) return true;
        if ("deployment_id" in context && hasDeploymentPermission(permission, context, feature)) return true;
      }
      return false;
    },
    [hasDeploymentPermission, hasGlobalPermission, hasWorkspacePermission],
  );

  const hasPermissions = useCallback(
    (permissions: Permission[], context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      for (const perm of permissions) {
        if (!hasPermission(perm, context, feature)) return false;
      }
      return true;
    },
    [hasPermission],
  );

  const canEditDeployment = useCallback(
    (context?: DeploymentScope, feature?: FeaturePermission): boolean => {
      if (!hasPermission(Permission.Edit, context, feature)) return false;
      return true;
    },
    [hasPermission],
  );
  const canEditWorkspace = useCallback(
    (context?: WorkspaceScope, feature?: FeaturePermission): boolean => {
      if (!hasPermission(Permission.Edit, context, feature)) return false;
      return true;
    },
    [hasPermission],
  );
  const canEdit = useCallback(
    (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      if (!hasPermission(Permission.Edit, context, feature)) return false;
      return true;
    },
    [hasPermission],
  );

  const canViewDeployment = (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
    if (!hasPermission(Permission.View, context, feature)) return false;
    return true;
  };
  const canViewWorkspace = (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
    if (!hasPermission(Permission.View, context, feature)) return false;
    return true;
  };
  const canView = (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
    if (!hasPermission(Permission.View, context, feature)) return false;
    return true;
  };

  const canEditCurrentWorkspace = useMemo(() => {
    if (currentWorkspace) return canEdit({ workspace_id: currentWorkspace.id });
    return false;
  }, [canEdit, currentWorkspace]);

  const canEditCurrentDeployment = useMemo(() => {
    if (currentDeployment) return canEdit({ deployment_id: currentDeployment.id });
    return false;
  }, [canEdit, currentDeployment]);

  const canEditCurrentWorkspaceOrFilteredDeployments = useMemo(() => {
    let canEditCurrentWorkspaceOrFilteredDeployments = false;
    if (currentWorkspace) {
      canEditCurrentWorkspaceOrFilteredDeployments = canEditWorkspace({
        workspace_id: currentWorkspace.id,
      });
    }

    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        canEditCurrentWorkspaceOrFilteredDeployments =
          canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d });
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        canEditCurrentWorkspaceOrFilteredDeployments =
          canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d.id });
      });
    }

    return canEditCurrentWorkspaceOrFilteredDeployments;
  }, [canEditDeployment, canEditWorkspace, currentWorkspace, filteredDeployments, options.deployments]);

  const canEditFilteredDeployments = useMemo(() => {
    let canEditFilteredDeployments = false;

    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        canEditFilteredDeployments = canEditFilteredDeployments || canEditDeployment({ deployment_id: d });
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        canEditFilteredDeployments = canEditFilteredDeployments || canEditDeployment({ deployment_id: d.id });
      });
    }

    return canEditFilteredDeployments;
  }, [canEditDeployment, filteredDeployments, options.deployments]);

  const canEditFeatureInCurrentWorkspaceOrFilteredDeployments = (feature: FeaturePermission) => {
    let canEditCurrentWorkspaceOrFilteredDeployments = false;
    if (currentWorkspace) {
      canEditCurrentWorkspaceOrFilteredDeployments = canEditWorkspace(
        {
          workspace_id: currentWorkspace.id,
        },
        feature,
      );
    }
    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        canEditCurrentWorkspaceOrFilteredDeployments =
          canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d }, feature);
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        canEditCurrentWorkspaceOrFilteredDeployments =
          canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d.id }, feature);
      });
    }
    return canEditCurrentWorkspaceOrFilteredDeployments;
  };

  const canEditFeatureInFilteredDeployments = (feature: FeaturePermission) => {
    let canEditFeatureInFilteredDeployments = false;
    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        canEditFeatureInFilteredDeployments =
          canEditFeatureInFilteredDeployments || canEditDeployment({ deployment_id: d }, feature);
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        canEditFeatureInFilteredDeployments =
          canEditFeatureInFilteredDeployments || canEditDeployment({ deployment_id: d.id }, feature);
      });
    }
    return canEditFeatureInFilteredDeployments;
  };

  return {
    availableWorkspaceRoles,
    availableDeploymentRoles,
    hasPermission,
    hasPermissions,
    canEdit,
    canEditDeployment,
    canEditWorkspace,
    canView,
    canViewDeployment,
    canViewWorkspace,
    canEditCurrentWorkspace,
    canEditCurrentDeployment,
    canEditCurrentWorkspaceOrFilteredDeployments,
    canEditFilteredDeployments,
    canEditFeatureInCurrentWorkspaceOrFilteredDeployments,
    canEditFeatureInFilteredDeployments,
  };
};
